import React from "react";
import { Link } from "react-router-dom";
import { Link2, PencilIcon } from "lucide-react";
import { Badge } from "src/shadcn/ui/badge";
import { DD_MM_YYYY } from "src/helpers";
import { UserColumnHeader } from "src/enums";
import { USERS_ROUTES_CONFIG, VENDORS_ROUTES_CONFIG } from "src/constants/routesConfig";
import { Table } from "src/components";
import { TableColumn, User } from "src/types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";

const getStatusBadgeVariant = (status?: User["status"]): "success" | "warning" | "destructive" | "secondary" => {
  switch (status) {
    case "Active":
      return "success";
    case "Provisioned":
      return "warning";
    case "Deactivated":
      return "destructive";
    default:
      return "secondary";
  }
};

const formatRole = (role: User["role"]): string => {
  const roleString = role as string;
  if (roleString === "ADMIN") return "Admin";
  if (roleString === "VENDOR") return "Vendor";
  if (roleString === "WAREHOUSE") return "Warehouse";
  if (roleString === "HEALF_ASSOCIATE") return "Associate";
  if (roleString === "HEALF_ACCOUNTANT") return "Accountant";
  if (roleString === "HEALF_OPERATIONS") return "Operations";
  if (roleString === "HEALF_OPERATIONS_MANAGER") return "Operations Manager";
  if (roleString === "HEALF_ASSOCIATE_MANAGER") return "Associate Manager";
  if (roleString === "NONE") return "None";
  return roleString.replace(/_/g, " ").replace(/HEALF/g, "").trim();
};

interface UsersDashTableProps {
  users: User[];
  columnsToHide: UserColumnHeader[];
}

const displayOrDash = (value?: string) => (value && value.trim().length > 0 ? value : "-");

const formatDateOrDash = (value?: string) => (value ? DD_MM_YYYY.formatDateAndAppendTime(new Date(value)) : undefined);

const chipClass =
  "inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900";

const UsersDashTable: React.FC<UsersDashTableProps> = ({ users, columnsToHide }) => {
  const centerStyles = { textAlign: "center" as const };
  const wrapStyles = { ...centerStyles, whiteSpace: "nowrap" as const };

  const usersTableColumns: TableColumn<UserColumnHeader, User>[] = [
    {
      header: UserColumnHeader.NAME,
      column: (row) => <div className="font-medium text-center">{displayOrDash(row.name)}</div>,
      styles: centerStyles
    },
    {
      header: UserColumnHeader.EMAIL,
      column: (row) => <span className="text-sm">{row.email}</span>,
      styles: centerStyles
    },
    {
      header: UserColumnHeader.ROLE,
      column: (row) => (
        <span className={`${chipClass} capitalize mx-auto`} style={{ display: "inline-block" }}>
          {formatRole(row.role)}
        </span>
      ),
      styles: centerStyles
    },
    {
      header: UserColumnHeader.STATUS,
      column: (row) => (
        <Badge variant={getStatusBadgeVariant(row.status)} className="font-medium">
          {displayOrDash(row.status)}
        </Badge>
      ),
      styles: { textAlign: "center" }
    },
    {
      header: UserColumnHeader.JOB_TITLE,
      column: (row) => <span className="text-sm">{displayOrDash(row.jobTitle)}</span>,
      styles: centerStyles
    },
    {
      header: UserColumnHeader.SUPERVISORS,
      column: (row) => {
        const emails = row.supervisorEmails?.filter(Boolean) ?? [];
        if (!emails.length) {
          return <span className="text-muted-foreground text-center w-full block">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1 justify-center">
            {emails.map((email) => (
              <span key={email} className={chipClass}>
                {email}
              </span>
            ))}
          </div>
        );
      },
      styles: centerStyles
    },
    {
      header: UserColumnHeader.VENDOR_IDS,
      column: (row) => {
        const vendorLabels = row.vendorNames?.length && row.vendorNames.some(Boolean) ? row.vendorNames : row.vendorIds;

        if (!vendorLabels || vendorLabels.length === 0) {
          return <span className="text-muted-foreground text-center w-full block">-</span>;
        }

        return (
          <div className="flex flex-wrap gap-1 justify-center max-w-[240px] mx-auto">
            {vendorLabels.map((label, index) => (
              <span key={`${label ?? "vendor"}-${index}`} className={chipClass}>
                {label ?? "-"}
              </span>
            ))}
          </div>
        );
      },
      styles: { ...centerStyles, minWidth: "240px" }
    },
    {
      header: UserColumnHeader.CREATED_BY_EMAIL,
      column: (row) => <span className="text-sm">{displayOrDash(row.createdByUserEmail)}</span>,
      styles: centerStyles
    },
    {
      header: UserColumnHeader.CREATED_AT,
      column: (row) => {
        const formatted = formatDateOrDash(row.createdAt);
        return <span className="text-sm text-muted-foreground">{formatted ?? "-"}</span>;
      },
      styles: wrapStyles
    },
    {
      header: UserColumnHeader.UPDATED_AT,
      column: (row) => {
        const formatted = formatDateOrDash(row.updatedAt);
        return <span className="text-sm text-muted-foreground">{formatted ?? "-"}</span>;
      },
      styles: wrapStyles
    },
    {
      header: UserColumnHeader.SIGNED_UP_AT,
      column: (row) => {
        const formatted = formatDateOrDash(row.signedUpAt);
        return <span className="text-sm text-muted-foreground">{formatted ?? "-"}</span>;
      },
      styles: wrapStyles
    },
    {
      header: UserColumnHeader.ACTIONS,
      column: (row) => {
        const vendorName =
          row.role === "VENDOR"
            ? row.vendorNames?.find((name) => typeof name === "string" && name.trim().length > 0)
            : undefined;

        return (
          <TooltipProvider>
            <div className="flex items-center justify-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`${USERS_ROUTES_CONFIG.updateUserAccount.path}?email=${encodeURIComponent(row.email)}`}
                    className="inline-flex items-center justify-center rounded-md border border-secondary-200 bg-white px-2 py-1 text-xs font-semibold text-secondary-700 transition-colors hover:bg-secondary-50"
                    aria-label={`Edit ${row.name ?? row.email}`}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit User</p>
                </TooltipContent>
              </Tooltip>

              {vendorName && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`${VENDORS_ROUTES_CONFIG.updateVendor.path}?name=${encodeURIComponent(vendorName)}`}
                      className="inline-flex items-center justify-center rounded-md border border-secondary-200 bg-white px-2 py-1 text-xs font-semibold text-secondary-700 transition-colors hover:bg-secondary-50"
                      aria-label={`Edit vendor ${vendorName}`}
                    >
                      <Link2 className="h-4 w-4" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit Vendor</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        );
      },
      styles: centerStyles
    }
  ];

  return <Table<UserColumnHeader, User> data={users} columns={usersTableColumns} columnsToHide={columnsToHide} />;
};

export { UsersDashTable };
